import React, { useState, useEffect } from 'react';
import './userlistcontent.css';
import { Link as RouterLink } from 'react-router-dom';
import defaultimg from '../../../assets/defaultimg.webp';

const UserListContent = () => {
    const [users, setUsers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('username');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const usersPerPage = 10;

    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_URL}/userlist`);
                
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                
                const data = await response.json();
                
                // Ensure skills is always an array
                const processedData = data.map(user => ({
                    ...user,
                    skills: Array.isArray(user.skills) ? user.skills : []
                }));
                
                setUsers(processedData);
                setError(null);
            } catch (error) {
                console.error('Error fetching users:', error);
                setError('Failed to load users. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        
        fetchUsers();
    }, [API_URL]);

    // Sorting function
    const sortUsers = (usersToSort) => {
        return [...usersToSort].sort((a, b) => {
            switch (sortField) {
                case 'username':
                    return (a.username || '').localeCompare(b.username || '');
                case 'name':
                    const nameA = `${a.first_name || ''} ${a.last_name || ''}`;
                    const nameB = `${b.first_name || ''} ${b.last_name || ''}`;
                    return nameA.localeCompare(nameB);
                case 'course':
                    return (a.course || '').localeCompare(b.course || '');
                case 'year':
                    return (a.mat_year || 0) - (b.mat_year || 0);
                case 'skills':
                    // Sort by number of skills (most skills first)
                    return (b.skills?.length || 0) - (a.skills?.length || 0);
                default:
                    return 0;
            }
        });
    };

    // Search function with safety checks
    const filterUsers = (usersToFilter) => {
        if (!searchTerm.trim()) return usersToFilter;
        
        const searchString = searchTerm.toLowerCase();
        
        return usersToFilter.filter(user => {
            // Safe checks for all properties
            const usernameMatch = (user.username || '').toLowerCase().includes(searchString);
            const firstNameMatch = (user.first_name || '').toLowerCase().includes(searchString);
            const lastNameMatch = (user.last_name || '').toLowerCase().includes(searchString);
            const courseMatch = (user.course || '').toLowerCase().includes(searchString);
            
            // Safe check for skills array
            const skillsMatch = Array.isArray(user.skills) && 
                user.skills.some(skill => 
                    typeof skill === 'string' && 
                    skill.toLowerCase().includes(searchString)
                );
            
            return usernameMatch || firstNameMatch || lastNameMatch || courseMatch || skillsMatch;
        });
    };

    // Combine filtering and sorting
    const processedUsers = sortUsers(filterUsers(users));

    // Pagination logic
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = processedUsers.slice(indexOfFirstUser, indexOfLastUser);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1); // Reset to first page when searching
    };

    // Function to render skills with a max display limit
    const renderSkills = (skills) => {
        if (!Array.isArray(skills) || skills.length === 0) return 'No skills listed';
        
        const maxDisplay = 3; // Maximum number of skills to display
        const displayedSkills = skills.slice(0, maxDisplay);
        const remainingCount = skills.length - maxDisplay;
        
        return ( // Render skills as tags
            <div className="skills-container">
                {displayedSkills.map((skill, index) => (
                    <span key={index} className="skill-tag">
                        {skill}
                    </span>
                ))}
                {remainingCount > 0 && (
                    <span className="skill-tag more-skills">
                        +{remainingCount} more
                    </span>
                )}
            </div>
        );
    };

    return ( // Main component rendering
        <div className="userlistbody">
            <div className="userlist-grid-container">
                <div className="userlist-grid-item userlist-grid-1">
                    <h3 style={{ fontWeight: '400' }}>User list</h3>
                    <form className="userlistsearchForm" onSubmit={handleSearch}>
                        <input
                            type="text"
                            className="userlistsearchBar"
                            placeholder="Search for users, skills..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button type="submit" className="userlistsearchBtn">Go</button>
                    </form>
                </div>

                <div className="userlist-grid-container2">
                    <div className="userlist-grid-item userlist-grid-3">
                        <div className="userlistresultstitle">
                            <h3 style={{ fontWeight: '400' }}>Top Results</h3>
                            <span style={{ fontSize: 'small', marginLeft: '1rem', fontWeight: '350' }}>
                                {loading ? 'Loading...' : 
                                 error ? 'Error loading users' :
                                 processedUsers.length === 0 ? 'No results' :
                                 `${indexOfFirstUser + 1}-${Math.min(indexOfLastUser, processedUsers.length)} out of ${processedUsers.length} results`
                                }
                            </span>
                            <div className="userlistsorting">
                                <span style={{ fontWeight: '400', fontSize: '0.9rem' }}>Sort by:</span>
                                <select 
                                    className="userroleselector"
                                    value={sortField}
                                    onChange={(e) => setSortField(e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="username">Username</option>
                                    <option value="name">Name</option>
                                    <option value="course">Course</option>
                                    <option value="year">Year</option>
                                    <option value="skills">Skills</option>
                                </select>
                            </div>
                        </div>
                        <hr style={{ margin: '1rem 0' }} />
                        
                        {loading ? (
                            <div className="loading-spinner">Loading users...</div>
                        ) : error ? (
                            <div className="error-message">{error}</div>
                        ) : ( // Display user results
                            <div className="userlistresults">
                                <div className="userbartitle-grid-container">
                                    <div className="userbartitle-grid-1">
                                        <span>Username</span>
                                    </div>
                                    <div className="userbartitle-grid-2">
                                        <span>Name</span>
                                    </div>
                                    <div className="userbartitle-grid-3">
                                        <span>Course</span>
                                    </div>
                                    <div className="userbartitle-grid-4">
                                        <span>Year</span>
                                    </div>
                                    <div className="userbartitle-grid-5">
                                        <span>Top Skills</span>
                                    </div>
                                </div>

                                {currentUsers.length === 0 ? (
                                    <div className="no-results">No users match your search criteria</div>
                                ) : ( // Map through current users and display their information
                                    currentUsers.map((user) => (
                                        <div className="userbarresult-grid-container" key={user.user_id}>
                                            <div className="userbarresult-grid-1">
                                                <RouterLink to={`/user/${user.user_id}`}>
                                                    <span className="username-link">{user.username}</span>
                                                </RouterLink>
                                            </div>
                                            <div className="userbarresult-grid-2">
                                                <img 
                                                    src={user.profile_pic || defaultimg} 
                                                    alt={`${user.first_name}'s profile`} 
                                                    className="userimg" 
                                                />
                                                <span>{user.first_name} {user.last_name}</span>
                                            </div>
                                            <div className="userbarresult-grid-3">
                                                <span>{user.course || 'N/A'}</span>
                                            </div>
                                            <div className="userbarresult-grid-4">
                                                <span>{user.mat_year || 'N/A'}</span>
                                            </div>
                                            <div className="userbarresult-grid-5">
                                                {renderSkills(user.skills)}
                                            </div>
                                        </div>
                                    ))
                                )}

                                <div className="userlistpagination">
                                    {Array.from({ length: Math.ceil(processedUsers.length / usersPerPage) }, (_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => paginate(index + 1)}
                                            className={currentPage === index + 1 ? 'active' : ''}
                                            disabled={loading}
                                        >
                                            {index + 1}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserListContent;